import { z } from 'zod';
import { BareEventSchema } from './event';
import { BareContentSchema } from './content';
import { DaySchema, TimeSchema } from './time';
import { PropertySchema } from './property';
import { objectIdSchema } from './objectId';
import { EventDisplayOption } from './eventTemplate';
import { McmSchema } from './mcm';
import { AudienceSchema } from './audience';
import { VenueTemplateSchema } from './venueTemplate';
import { DailyProgramCtaSchema } from './dailyProgramCta';
import { VenueHoursSchema } from './venueHours';
import { InvertedVenueHoursStatus } from './const';

export const InvertedVenueHourSchema = z.object({
  startTime: TimeSchema,
  endTime: TimeSchema,
  status: z.enum(InvertedVenueHoursStatus),
});

export const VenueMealPeriodSchema = z.object({
  startTime: TimeSchema,
  endTime: TimeSchema,
  name: z.string(),
  code: z.string().optional(),
  brandMealPeriodId: objectIdSchema,
  needsReservation: z.boolean().optional(),
});

export const VenueTimeDisplayOptions = EventDisplayOption;

export const VenueDisplayOptionsSchema = z.object({
  displayName: z.string().optional(),
  deckDisplayName: z.string().optional(),
  timeDisplay: z.union([z.enum(VenueTimeDisplayOptions), z.string()]).optional(),
});

export const VenueDispositionSchema = z.object({
  _id: objectIdSchema,
  template: objectIdSchema,
  hours: VenueHoursSchema.array(),
  mealPeriods: VenueMealPeriodSchema.array(),
  callToActions: DailyProgramCtaSchema.array(),
  displayOptions: VenueDisplayOptionsSchema.optional(),
  dynamic: z.boolean().optional(),
  mcm: McmSchema.optional(),
  audience: AudienceSchema.optional(),
});

export const PlanVenueConfigurationDayHoursSchema = z.object({
  startTime: TimeSchema,
  endTime: TimeSchema,
  configuration: objectIdSchema, // object id pointing to venue configuration,
});

export const PlanVenueConfigurationDaySchema = z.object({
  days: objectIdSchema, // object id pointing to day
  hours: PlanVenueConfigurationDayHoursSchema.array(),
});

export const PlanVenueConfigurationSchema = z.object({
  template: objectIdSchema, // object id pointing to template
  days: PlanVenueConfigurationDaySchema.array(),
});

export const BarePlanSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  events: BareEventSchema.array(),
  venueData: VenueDispositionSchema.array(),
  content: BareContentSchema.array(),
  venueConfigurations: PlanVenueConfigurationSchema.array(),
  day: DaySchema,
  dayStart: TimeSchema,
  property: objectIdSchema,
});

export const BareProjectSchema = BarePlanSchema;

export const PlanSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  events: z.array(BareEventSchema),
  venueData: z.array(VenueDispositionSchema),
  content: z.array(BareContentSchema),
  venueConfigurations: z.array(PlanVenueConfigurationSchema),
  day: DaySchema,
  dayStart: TimeSchema,
  property: PropertySchema,
});

export const ProjectSchema = PlanSchema;

export const PlanWithVenuesSchema = PlanSchema.and(
  z.object({
    venues: z.array(VenueTemplateSchema),
  })
);

export type BarePlan = z.infer<typeof BarePlanSchema>;
